import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Workflow } from "lucide-react";
import { flowsApi, type ActiveFlow } from "../api/flowsApi";
import FlowBuilderApp from "../flowbuilder/App";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; flow: ActiveFlow }
  | { status: "missing"; message: string }
  | { status: "error"; message: string };

/**
 * Hosts the in-SPA Flow Builder for a single application. The route is
 * /applications/:applicationId/design — there is no separate "flows" picker
 * because the user always edits the application's currently active flow
 * (Application.InitialFlowId).
 *
 * UX: the designer takes over the full content area between the global
 * TopBar and Footer, with no left Sidebar and no breadcrumb. To make the
 * vertical chain (app-shell -> main -> page) clamp the FlowBuilder to the
 * exact remaining space (so its internal sidebars scroll instead of the
 * page), we toggle a `body.fb-fullbleed` class while this page is mounted.
 * The matching CSS in app.css turns the global flex column into a fixed
 * 100dvh frame with `overflow: hidden`.
 */
export function FlowDesignerPage() {
  const { applicationId: rawId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const applicationId = Number(rawId);
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    document.body.classList.add("fb-fullbleed");
    return () => {
      document.body.classList.remove("fb-fullbleed");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!applicationId || Number.isNaN(applicationId)) {
      setState({ status: "missing", message: "Invalid application." });
      return;
    }
    setState({ status: "loading" });
    flowsApi
      .getActiveFlow(applicationId)
      .then((flow) => {
        if (cancelled) return;
        setState({ status: "ready", flow });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : "Could not load the active flow.";
        if (typeof err === "object" && err && "status" in err) {
          const status = (err as { status?: number }).status;
          if (status === 404) {
            setState({
              status: "missing",
              message:
                "This application does not have an active flow yet. Create one first in the legacy portal.",
            });
            return;
          }
        }
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  if (state.status === "loading") {
    return (
      <div className="flowbuilder-page flowbuilder-page--placeholder">
        <span style={{ color: "var(--portal-muted)" }}>Loading flow…</span>
      </div>
    );
  }

  if (state.status === "missing" || state.status === "error") {
    return <DesignerMessage tone={state.status === "error" ? "error" : "info"} message={state.message} />;
  }

  const flow = state.flow;
  return (
    <div className="flowbuilder-page">
      <FlowBuilderApp
        applicationId={flow.applicationId}
        flowId={flow.flowId}
        initialFlowName={flow.flowName ?? `Application ${flow.applicationId}`}
        onBack={() => navigate("/applications")}
      />
    </div>
  );
}

function DesignerMessage({
  tone,
  message,
}: {
  tone: "info" | "error";
  message: string;
}) {
  const toneStyles =
    tone === "error"
      ? { borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }
      : {};
  return (
    <div className="flowbuilder-page flowbuilder-page--placeholder">
      <section
        className="tile"
        style={{
          display: "grid",
          gridTemplateColumns: "56px 1fr",
          gap: 18,
          alignItems: "flex-start",
          padding: 24,
          maxWidth: 640,
          ...toneStyles,
        }}
      >
        <div
          className="tile-row-icon"
          style={{ width: 56, height: 56, borderRadius: 14 }}
          aria-hidden="true"
        >
          <Workflow size={28} />
        </div>
        <div>
          <h3 style={{ marginTop: 0 }}>Flow Designer</h3>
          <p style={{ marginTop: 0 }}>{message}</p>
          <Link to="/applications" className="btn btn-ghost">
            <ArrowLeft size={16} />
            Back to Applications
          </Link>
        </div>
      </section>
    </div>
  );
}
